import { normalizeScanStats } from '../utils/normalizeScanStats';
import { scannerFetch } from '../utils/scannerFetch';

class ApiService {
  private baseUrl: string;
  private licenseKey: string | null = null;
  private vpsIp: string | null = null;
  private vpsPort: string | null = null;

  constructor() {
    // URL de base pour les requêtes vers le serveur principal
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://glitchcloud.app';
  }

  setLicenseKey(licenseKey: string, vpsIp?: string, vpsPort?: string) {
    this.licenseKey = licenseKey;
    this.vpsIp = vpsIp || null;
    this.vpsPort = vpsPort || '3443'; // Port HTTPS par défaut
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.licenseKey) {
      headers['X-License-Key'] = this.licenseKey;
    }

    return headers;
  }

  // Vérification de licence
  async verifyLicense(licenseCode: string) {
    try {
      const hwid = this.generateHWID();
      
      const response = await fetch(`${this.baseUrl}/license/verify`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          licenseCode,
          hwid
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Configurer l'API service avec les infos VPS de la licence
        this.setLicenseKey(licenseCode, data.license.vpsIp, data.license.vpsPort);
      }
      
      return data;
    } catch (error) {
      console.error('Erreur vérification licence:', error);
      throw error;
    }
  }

  private generateHWID(): string {
    // Générer un HWID basique basé sur les informations du navigateur
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    // Créer un hash simple
    let hash = 0;
    const str = userAgent + platform + language;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return Math.abs(hash).toString(16);
  }

  // Gestion des domaines - Utilise directement le VPS Client
  async getDomains() {
    try {
      const response = await this.vpsRequest('/domains', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      // Gérer le nouveau format de réponse
      if (data.type === 'individual_domains') {
        return data.domains || [];
      } else if (data.type === 'file_summary') {
        // Pour les gros fichiers, retourner un tableau vide pour éviter les crashes
        return [];
      } else if (data.type === 'empty') {
        return [];
      } else {
        // Fallback pour l'ancien format
        return Array.isArray(data) ? data : [];
      }
    } catch (error) {
      console.error('Erreur récupération domaines:', error);
      throw error;
    }
  }

  // Obtenir le nombre total de domaines
  async getDomainsCount() {
    try {
      const response = await this.vpsRequest('/domains/count', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.totalCount || 0;
    } catch (error) {
      console.error('Erreur comptage domaines:', error);
      return 0;
    }
  }

  async addDomain(domain: string) {
    try {
      const response = await this.vpsRequest('/domains', {
        method: 'POST',
        body: JSON.stringify({ domain }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur ajout domaine:', error);
      throw error;
    }
  }

  async deleteDomain(domainId: string) {
    try {
      const response = await this.vpsRequest(`/domains/${domainId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression domaine:', error);
      throw error;
    }
  }

  // Construire l'URL VPS pour les requêtes directes
  private getVpsUrl(endpoint: string): string {
    if (!this.vpsIp || !this.vpsPort) {
      throw new Error('IP VPS non configurée pour cette licence');
    }
    // Communication directe avec le VPS Client en HTTPS
    return `https://${this.vpsIp}:${this.vpsPort}${endpoint}`;
  }

  // Fonction utilitaire pour faire des requêtes VPS
  private async vpsRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    if (!this.vpsIp || !this.vpsPort) {
      throw new Error('IP VPS non configurée pour cette licence');
    }

    const isBodyMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
      (options.method || 'GET').toUpperCase(),
    );

    return scannerFetch(this.vpsIp, this.vpsPort, endpoint, {
      ...options,
      headers: {
        ...(isBodyMethod ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });
  }

  // Gestion du scan - Utilise directement le VPS Client
  async getScanStatus() {
    try {
      const response = await this.vpsRequest('/status', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return normalizeScanStats(await response.json());
    } catch (error) {
      console.error('Erreur récupération statut scan:', error);
      throw error;
    }
  }

  async getRealTimeStats() {
    try {
      const response = await this.vpsRequest('/realtime-stats', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.success ? normalizeScanStats(data.stats) : null;
    } catch (error) {
      console.error('Erreur récupération stats temps réel:', error);
      throw error;
    }
  }

  async getScanResults() {
    try {
      const response = await this.vpsRequest('/results', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération résultats scan:', error);
      throw error;
    }
  }

  // Gestion des hits - Utilise directement le VPS Client
  async getHits(type: string = 'all', limit?: number) {
    try {
      let endpoint = `/api/hits?type=${type}`;
      if (limit) {
        endpoint += `&limit=${limit}`;
      }
      
      const response = await this.vpsRequest(endpoint, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération hits:', error);
      throw error;
    }
  }

  async deleteHit(hitId: string) {
    try {
      const response = await this.vpsRequest(`/api/hits/${hitId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression hit:', error);
      throw error;
    }
  }

  async markHitAsViewed(hitId: string) {
    try {
      const response = await this.vpsRequest(`/api/hits/${hitId}/view`, {
        method: 'PATCH'
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur marquage hit:', error);
      throw error;
    }
  }

  async recheckHit(hitId: string) {
    try {
      const response = await this.vpsRequest(`/api/hits/${encodeURIComponent(hitId)}/recheck`, {
        method: 'POST',
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Recheck error ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Erreur recheck hit:', error);
      throw error;
    }
  }

  async clearInvalidHits() {
    try {
      const response = await this.vpsRequest('/api/hits/invalid/clear', {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression hits invalides:', error);
      throw error;
    }
  }

  async clearAllHits() {
    try {
      const response = await this.vpsRequest('/api/hits/all/clear', {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression tous les hits:', error);
      throw error;
    }
  }

  async startScan(domains: string[]) {
    try {
      const response = await this.vpsRequest('/start-scan', {
        method: 'POST',
        body: JSON.stringify({ domains }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur démarrage scan:', error);
      throw error;
    }
  }

  async stopScan() {
    try {
      const response = await this.vpsRequest('/stop-scan', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur arrêt scan:', error);
      throw error;
    }
  }

  // Gestion des paiements (proxy vers le serveur de paiement)
  async createPayment(paymentData: any) {
    try {
      const response = await fetch(`${this.baseUrl}/api/payment/create`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur création paiement:', error);
      throw error;
    }
  }

  async getPaymentStatus(paymentId: string) {
    try {
      const response = await fetch(`${this.baseUrl}/api/payment/status/${paymentId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération statut paiement:', error);
      throw error;
    }
  }

  async checkPayment(paymentId: string) {
    try {
      const response = await fetch(`${this.baseUrl}/api/payment/check/${paymentId}`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      throw error;
    }
  }

  async getPayments() {
    try {
      const response = await fetch(`${this.baseUrl}/api/payments`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération paiements:', error);
      throw error;
    }
  }

  // Gestion des configurations VPS
  async getVpsConfigs() {
    try {
      const response = await fetch(`${this.baseUrl}/api/vps-configs`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération configs VPS:', error);
      throw error;
    }
  }

  async addVpsConfig(configData: any) {
    try {
      const response = await fetch(`${this.baseUrl}/api/vps-config/add`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(configData),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur ajout config VPS:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      console.error('Erreur health check:', error);
      return false;
    }
  }

  // Configuration Telegram
  async getTelegramConfig() {
    try {
      const response = await this.vpsRequest('/telegram-config', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const vpsConfig = await response.json();
      
      // Convertir les noms de champs du serveur VPS vers ceux attendus par le frontend
      return {
        botToken: vpsConfig.botToken || '',
        channelValid: vpsConfig.validHitsChannelId || '',
        channelInvalid: vpsConfig.invalidHitsChannelId || '',
        channelStats: vpsConfig.statsChannelId || ''
      };
    } catch (error) {
      console.error('Erreur récupération config Telegram:', error);
      throw error;
    }
  }

  async updateTelegramConfig(config: {
    botToken: string;
    channelValid: string;
    channelInvalid: string;
    channelStats: string;
  }) {
    try {
      // Convertir les noms de champs pour correspondre à ce que le serveur VPS attend
      const vpsConfig = {
        botToken: config.botToken,
        validHitsChannelId: config.channelValid,
        invalidHitsChannelId: config.channelInvalid,
        statsChannelId: config.channelStats
      };
      
      const response = await this.vpsRequest('/telegram-config', {
        method: 'POST',
        body: JSON.stringify(vpsConfig),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur mise à jour config Telegram:', error);
      throw error;
    }
  }

  // SMTP Test Email Address (appsettings.json > KeyFinder.SmtpTestEmailAddress)
  async getSmtpEmail(): Promise<{ smtpTestEmailAddress: string }> {
    try {
      const response = await this.vpsRequest('/smtp-email', { method: 'GET' });
      if (!response.ok) return { smtpTestEmailAddress: '' };
      return await response.json();
    } catch {
      return { smtpTestEmailAddress: '' };
    }
  }

  async updateSmtpEmail(email: string): Promise<void> {
    const response = await this.vpsRequest('/smtp-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  // Liste des fichiers disponibles dans le dossier Check
  async getCheckFiles() {
    try {
      const response = await this.vpsRequest('/check-files', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur récupération fichiers Check:', error);
      throw error;
    }
  }

  async deleteCheckFile(fileName: string) {
    try {
      const response = await this.vpsRequest(`/check-files/${encodeURIComponent(fileName)}`, { 
        method: 'DELETE' 
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression fichier Check:', error);
      throw error;
    }
  }

  async downloadCheckFile(fileName: string) {
    try {
      const response = await this.vpsRequest(`/check-files/${encodeURIComponent(fileName)}/download`, { 
        method: 'GET' 
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Obtenir le contenu du fichier en texte
      const content = await response.text();
      
      // Créer un blob et déclencher le téléchargement
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Fichier téléchargé avec succès' };
    } catch (error) {
      console.error('Erreur téléchargement fichier Check:', error);
      throw error;
    }
  }

  async uploadDomainsFile(formData: FormData) {
    try {
      const response = await fetch(this.getVpsUrl('/domains/upload'), {
        method: 'POST',
        body: formData,
        headers: {
          'X-License-Key': this.licenseKey || '',
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur upload fichier domaines:', error);
      throw error;
    }
  }

  // Gestion des proxies
  async getProxies() {
    try {
      const response = await this.vpsRequest('/proxies', { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.proxies || [];
    } catch (error) {
      console.error('Erreur récupération proxies:', error);
      return [];
    }
  }

  async addProxies(proxyList: string) {
    try {
      const response = await this.vpsRequest('/proxies', {
        method: 'POST',
        body: JSON.stringify({ proxyList }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur ajout proxies:', error);
      throw error;
    }
  }

  async deleteAllProxies() {
    try {
      const response = await this.vpsRequest('/proxies', {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur suppression proxies:', error);
      throw error;
    }
  }

  // WebSocket connection pour les hits en temps réel
  createHitsWebSocketConnection() {
    if (!this.vpsIp || !this.vpsPort) {
      throw new Error('IP VPS non configurée pour cette licence');
    }
    
    const wsUrl = `wss://${this.vpsIp}:${this.vpsPort}/ws/hits`;
    return new WebSocket(wsUrl);
  }

  // WebSocket connection
  createWebSocketConnection() {
    if (!this.licenseKey) {
      throw new Error('Licence non configurée');
    }

    const wsUrl = this.baseUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    return new WebSocket(`${wsUrl}/ws?license=${this.licenseKey}`);
  }
}

const apiService = new ApiService();
export default apiService; 